import { useEffect, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FeaturedService } from '@/models/service.ts'
import { CardWithImage } from '../../../components/CardWithImage'
import { HtmlContent } from '../../../components/HtmlContent'
import { InputQuantity } from '../../../components/InputQuantity'
import { priceFormatter } from '../../../utils/price.ts'

interface CardExtraProps {
  initialQuantity: number
  maxQuantity: number
  minQuantity: number
  onQuantityChange(quantity: number): void
  service: FeaturedService
}

export function CardExtra(props: CardExtraProps) {
  const { t } = useTranslation()

  const [quantitySelected, setQuantitySelected] = useState(
    Math.max(props.initialQuantity, props.minQuantity)
  )

  useEffect(() => {
    props.onQuantityChange(quantitySelected)
  }, [quantitySelected])

  const featureListDescription = useId()
  const priceId = useId()
  const articleTitleId = useId()

  const price = ((): string | number => {
    if (props.service.price === 0 && props.maxQuantity === props.minQuantity) {
      return props.maxQuantity === 0 ? '' : t('extra.included')
    }
    return props.service.price
  })()

  return (
    <CardWithImage
      aria-labelledby={`${articleTitleId} ${priceId}`}
      contentWrapperProps={{ className: 'flex flex-col justify-between gap-4' }}
      image={{ alt: '', src: props.service.image }}
    >
      <div>
        <h3
          className="mb-1 break-all text-lg font-bold leading-6"
          id={articleTitleId}
        >
          {props.service.name}
        </h3>

        <HtmlContent
          className="text-sm leading-6"
          description={props.service.description}
          id={featureListDescription}
        />
      </div>

      <div className="flex justify-between">
        <div>
          <h4 className="sr-only">{t('a11y.priceAndMaxQuantity')}</h4>
          <data className="text-lg font-bold" id={priceId} value={price}>
            {typeof price === 'number'
              ? priceFormatter.format(props.service.price)
              : price}
          </data>

          {props.service.hasLimit && props.minQuantity !== props.maxQuantity ? (
            <p className="text-xs">
              {t('extra.maxQuantity', { maxQuantity: props.maxQuantity })}
            </p>
          ) : null}
        </div>

        <InputQuantity
          max={props.maxQuantity}
          min={props.minQuantity}
          productName={props.service.name}
          quantity={quantitySelected}
          quantityChange={setQuantitySelected}
        />
      </div>
    </CardWithImage>
  )
}
